/**
 * Les surplus de chantier (V1.6-10B).
 *
 * Cet écran répond à « qu'est-ce qui reste sur nos chantiers, et qu'est-ce qui
 * attend une décision ». Il ne déclare rien lui-même : une déclaration naît sur
 * le terrain, là où quelqu'un voit ce qui reste.
 *
 * **La colonne qui compte est « Reste ».** Déclaré dit ce qu'on a trouvé,
 * disposé ce qu'on a réglé ; c'est l'écart entre les deux qui dort sur un
 * chantier. Les trois chiffres viennent du serveur — l'écran ne soustrait rien.
 */
import { Link, useSearchParams } from "react-router-dom";
import { PackageOpen } from "lucide-react";

import { useProjectSurpluses } from "@/api/project-surpluses";
import type { ProjectSurplus, ProjectSurplusStatus } from "@/api/types";
import { Pagination, TableShell, Td, Th, Tr } from "@/components/DataTable";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import { formatDate, formatSurplusStatus, pluralize } from "@/lib/format";
import { surplusTone } from "@/lib/stock";

const PAGE_SIZE = 50;

const FILTERS: { value: "" | ProjectSurplusStatus; label: string }[] = [
  { value: "", label: "Tous" },
  { value: "DRAFT", label: "Brouillons" },
  { value: "OPEN", label: "À décider" },
  { value: "CANCELLED", label: "Annulés" },
];

/** Déclaré, disposé, reste — sommés sur les lignes, par le serveur. */
function totals(surplus: ProjectSurplus) {
  let declared = 0;
  let disposed = 0;
  let remaining = 0;
  for (const line of surplus.lines) {
    declared += Number(line.quantity_declared);
    disposed += Number(line.disposed);
    remaining += Number(line.remaining);
  }
  return { declared, disposed, remaining };
}

export function ProjectSurplusesPage() {
  const [params, setParams] = useSearchParams();
  const offset = Number(params.get("offset") ?? 0);
  const status = (params.get("status") ?? "") as "" | ProjectSurplusStatus;

  const { data, isLoading, error, refetch } = useProjectSurpluses({
    limit: PAGE_SIZE,
    offset,
    ...(status ? { status } : {}),
  });

  function goTo(next: number) {
    const merged = new URLSearchParams(params);
    if (next === 0) merged.delete("offset");
    else merged.set("offset", String(next));
    setParams(merged, { replace: true });
  }

  function filter(next: "" | ProjectSurplusStatus) {
    const merged = new URLSearchParams(params);
    merged.delete("offset");
    if (next) merged.set("status", next);
    else merged.delete("status");
    setParams(merged, { replace: true });
  }

  return (
    <Page>
      <PageHeader
        title="Surplus de projet"
        subtitle="Ce qui reste sur les chantiers. Déclarer ne bouge rien ; c'est la décision, une fois exécutée, qui déplace la marchandise."
      />

      <div className="mb-3 flex flex-wrap gap-1.5">
        {FILTERS.map((item) => (
          <button
            key={item.value || "all"}
            type="button"
            onClick={() => filter(item.value)}
            className={`rounded-md border px-2.5 py-1 text-[12px] ${
              status === item.value
                ? "border-brand/40 bg-brand/10 text-brand"
                : "border-border text-ink-muted hover:text-ink"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
        <div className="min-w-0">
          {isLoading && <TableSkeleton rows={8} columns={6} />}
          {error && <ErrorState error={error} onRetry={() => void refetch()} />}

          {data && data.items.length === 0 && (
            <EmptyState
              icon={<PackageOpen className="size-6" aria-hidden />}
              title="Aucun surplus déclaré"
              description="Un surplus se déclare depuis le terrain, sur ce qui reste réellement à un endroit."
            />
          )}

          {data && data.items.length > 0 && (
            <>
              <div className="hidden md:block">
                <TableShell>
                  <thead>
                    <tr>
                      <Th className="w-32 whitespace-nowrap">Code</Th>
                      <Th className="max-w-0">Projet</Th>
                      <Th className="hidden max-w-0 @3xl:table-cell">Source</Th>
                      <Th>Statut</Th>
                      <Th align="right" className="whitespace-nowrap">
                        Articles
                      </Th>
                      <Th align="right" className="hidden whitespace-nowrap @4xl:table-cell">
                        Déclaré
                      </Th>
                      <Th align="right" className="hidden whitespace-nowrap @4xl:table-cell">
                        Disposé
                      </Th>
                      <Th align="right" className="whitespace-nowrap">
                        Reste
                      </Th>
                      <Th className="hidden whitespace-nowrap @5xl:table-cell">Déclaré le</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((item) => (
                      <SurplusRow key={item.id} surplus={item} />
                    ))}
                  </tbody>
                </TableShell>
              </div>

              <ul className="divide-y divide-border/60 md:hidden">
                {data.items.map((item) => (
                  <li key={item.id}>
                    <SurplusCard surplus={item} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {data && data.total > PAGE_SIZE && (
          <Pagination offset={offset} limit={PAGE_SIZE} total={data.total} onChange={goTo} />
        )}
      </div>
    </Page>
  );
}

function SurplusRow({ surplus }: { surplus: ProjectSurplus }) {
  const sums = totals(surplus);
  return (
    <Tr>
      <Td className="whitespace-nowrap font-mono text-[12px] text-ink-muted">
        <Link to={`/surplus/${surplus.id}`} className="hover:text-brand">
          {surplus.code}
        </Link>
      </Td>
      <Td className="max-w-0">
        <Link
          to={`/surplus/${surplus.id}`}
          className="block truncate font-medium text-ink hover:text-brand"
        >
          {surplus.project_name ?? "Chantier"}
        </Link>
        {surplus.project_code && (
          <span className="block truncate font-mono text-[11px] text-ink-subtle">
            {surplus.project_code}
          </span>
        )}
      </Td>
      <Td className="hidden max-w-0 truncate text-[13px] text-ink-muted @3xl:table-cell">
        {surplus.source_location_name ?? "—"}
      </Td>
      <Td>
        <StatusBadge tone={surplusTone(surplus.status)}>
          {formatSurplusStatus(surplus.status)}
        </StatusBadge>
      </Td>
      <Td align="right" className="tnum text-[13px] text-ink-muted">
        {surplus.line_count}
      </Td>
      <Td align="right" className="tnum hidden text-[13px] text-ink-muted @4xl:table-cell">
        {sums.declared}
      </Td>
      <Td align="right" className="tnum hidden text-[13px] text-ink-muted @4xl:table-cell">
        {sums.disposed}
      </Td>
      <Td
        align="right"
        className={`tnum text-[13px] ${sums.remaining > 0 ? "text-warning" : "text-ink-muted"}`}
      >
        {sums.remaining}
      </Td>
      <Td className="hidden whitespace-nowrap text-[12px] text-ink-subtle @5xl:table-cell">
        {surplus.declared_at ? formatDate(surplus.declared_at) : "—"}
      </Td>
    </Tr>
  );
}

/** Le même surplus, pour un pouce sur un iPhone. */
function SurplusCard({ surplus }: { surplus: ProjectSurplus }) {
  const sums = totals(surplus);
  return (
    <Link to={`/surplus/${surplus.id}`} className="block px-3 py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-[14px] font-medium text-ink">
            {surplus.project_name ?? "Chantier"}
          </p>
          <p className="font-mono text-[11px] text-ink-subtle">{surplus.code}</p>
        </div>
        <StatusBadge tone={surplusTone(surplus.status)}>
          {formatSurplusStatus(surplus.status)}
        </StatusBadge>
      </div>
      <dl className="tnum mt-1.5 grid grid-cols-3 gap-1 text-[12px]">
        <div>
          <dt className="text-ink-subtle">Déclaré</dt>
          <dd className="text-ink-muted">{sums.declared}</dd>
        </div>
        <div>
          <dt className="text-ink-subtle">Disposé</dt>
          <dd className="text-ink-muted">{sums.disposed}</dd>
        </div>
        <div>
          <dt className="text-ink-subtle">Reste</dt>
          <dd className={sums.remaining > 0 ? "text-warning" : "text-ink"}>{sums.remaining}</dd>
        </div>
      </dl>
      <p className="mt-1 truncate text-[12px] text-ink-subtle">
        {pluralize(surplus.line_count, "article", "articles")}
        {surplus.source_location_name ? ` · ${surplus.source_location_name}` : ""}
      </p>
    </Link>
  );
}
